import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { useNavigationItems } from '@/hooks/useNavigationItems';
import { useStoreData } from '@/queries/useStoreData';
import { PinnableItemsView, useMoreOptionsActions } from '@/features/more-options';

/**
 * More Options Page
 *
 * This page allows users to manage their pinned navigation items on desktop.
 * Unlike Vue, the more options page and mobile more menu is separate.
 * They serve completely different purposes and are not interchangeable.
 */
export default function MoreOptionsPage() {
    const params = useParams<{ storeId?: string }>();
    const { pathname } = useLocation();

    // Set breadcrumbs
    useBreadcrumbs([{ title: 'More Options', to: '/apps', id: 'more', active: true }]);

    // Get navigation items
    const { pinnableItems, adminItems, accountItems } = useNavigationItems();

    // Get actions
    const { handleItemClick, handleToggle, handleCommunityClick, isItemPinned, isToggleDisabled } =
        useMoreOptionsActions();

    // Store data management for route changes
    const storeId = params.storeId ? Number(params.storeId) : null;
    const { refetch: fetchStore, isLoading: getStoreLoading } = useStoreData(storeId);

    // Handle store refetch when route changes
    useEffect(() => {
        if (getStoreLoading) return;

        const globalStoreType = 'linksite';
        const globalStoreId = 'self';
        let routeStoreType: 'linksite' | 'funnels' = 'linksite';
        let routeStoreId = 'self';

        if (pathname.startsWith('/funnels') && params.storeId) {
            routeStoreId = params.storeId;
            routeStoreType = 'funnels';
        }

        const typeChanged = globalStoreType !== routeStoreType;
        const idChanged = routeStoreType === 'funnels' && globalStoreId !== routeStoreId;

        if (typeChanged || idChanged) {
            fetchStore();
        }
    }, [pathname, params.storeId, getStoreLoading]);

    return (
        <div className='mx-auto w-full max-w-screen-2xl'>
            <PinnableItemsView
                pinnableItems={pinnableItems}
                adminItems={adminItems}
                accountItems={accountItems}
                onItemClick={handleItemClick}
                onCommunityClick={handleCommunityClick}
                onToggle={handleToggle}
                isItemPinned={isItemPinned}
                isToggleDisabled={isToggleDisabled}
            />
        </div>
    );
}
